import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    GraduationCap,
    Briefcase,
    Users,
    Sparkles,
    Plus,
    Trash2,
    Save,
    Lock,
    ExternalLink,
    ArrowLeft,
} from "lucide-react";

/**
 * Resume editor — user-authored sections of the classic (Harvard-MCS style)
 * resume. Users cannot edit the "Verified Ledger" block; that section is
 * populated live from Atlas activity and is intentionally read-only so it
 * remains un-fakeable.
 */
export default function ResumeEditor() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [phone, setPhone] = useState("");
    const [showEmail, setShowEmail] = useState(false);
    const [education, setEducation] = useState([]);
    const [work, setWork] = useState([]);
    const [leadership, setLeadership] = useState([]);
    const [skills, setSkills] = useState({
        technical: "",
        languages: "",
        laboratory: "",
        interests: "",
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/me/resume-fields")
            .then(({ data }) => {
                setPhone(data.phone || "");
                setShowEmail(!!data.show_contact_email);
                setEducation(data.education || []);
                setWork(data.work_history || []);
                setLeadership(data.leadership || []);
                setSkills({
                    technical: data.skills_interests?.technical || "",
                    languages: data.skills_interests?.languages || "",
                    laboratory: data.skills_interests?.laboratory || "",
                    interests: data.skills_interests?.interests || "",
                });
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const addEdu = () =>
        setEducation((e) => [
            ...e,
            { institution: "", degree: "", concentration: "", gpa: "", dates: "", location: "", thesis: "", coursework: "", honors: "" },
        ]);
    const addWork = () =>
        setWork((w) => [...w, { organization: "", position: "", location: "", dates: "", bullets: [""] }]);
    const addLead = () =>
        setLeadership((l) => [...l, { organization: "", role: "", location: "", dates: "", bullets: [""] }]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                phone: phone || null,
                show_contact_email: showEmail,
                education: education.filter((e) => (e.institution || "").trim()),
                work_history: work.filter((w) => (w.organization || "").trim()),
                leadership: leadership.filter((l) => (l.organization || "").trim()),
                skills_interests: skills,
            };
            await api.put("/me/resume-fields", payload);
            toast.success("Resume fields saved");
        } catch (ex) {
            toast.error(ex.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!user) return null;
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500">Loading editor…</div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <TopBar />
            <main className="max-w-3xl mx-auto px-4 sm:px-8 py-8" data-testid="resume-editor">
                <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <Link
                            to={user?.username ? `/u/${user.username}/resume` : "/profile"}
                            className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-slate-900"
                        >
                            <ArrowLeft className="w-3 h-3" /> Back to resume
                        </Link>
                        <h1 className="mt-1 font-display font-black text-4xl tracking-tighter text-slate-900">
                            Edit resume
                        </h1>
                        <p className="text-sm text-slate-600 mt-1 max-w-xl">
                            Fill in the traditional sections. The Verified Ledger — everything you've
                            actually done on Atlas — is added automatically and can't be edited.
                        </p>
                    </div>
                    <button
                        onClick={save}
                        disabled={saving}
                        className="arcade-btn"
                        data-variant="primary"
                        data-testid="resume-editor-save"
                    >
                        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
                    </button>
                </header>

                {/* Locked notice about the Verified Ledger */}
                <div
                    className="rounded-2xl border-2 border-dashed border-slate-300 bg-white px-5 py-4 mb-6 flex items-start gap-3"
                    data-testid="resume-editor-ledger-note"
                >
                    <Lock className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-slate-700">
                        <div className="font-display font-bold text-slate-900">
                            Verified Ledger — auto-populated
                        </div>
                        <p className="mt-1">
                            Beacons, merit badges, master branches, and career paths are stamped by
                            Atlas as you work. You can view them on your{" "}
                            <Link to={user?.username ? `/u/${user.username}/resume` : "/profile"} className="text-humanities font-bold hover:underline inline-flex items-center gap-1">
                                public resume <ExternalLink className="w-3 h-3" />
                            </Link>
                            . They can't be edited here — that's the point.
                        </p>
                    </div>
                </div>

                {/* Contact */}
                <Section title="Contact" icon={<Sparkles className="w-4 h-4" />}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <TextInput
                            label="Phone"
                            value={phone}
                            onChange={setPhone}
                            testid="resume-phone"
                            placeholder="+1 555 555 5555"
                        />
                        <label className="flex items-start gap-2 mt-6">
                            <input
                                type="checkbox"
                                checked={showEmail}
                                onChange={(e) => setShowEmail(e.target.checked)}
                                data-testid="resume-show-email"
                            />
                            <span className="text-sm text-slate-700">
                                Show my login email on the public resume
                            </span>
                        </label>
                    </div>
                </Section>

                {/* Education */}
                <Section
                    title="Education"
                    icon={<GraduationCap className="w-4 h-4" />}
                    testid="resume-education-section"
                    onAdd={addEdu}
                >
                    {education.length === 0 && <Empty label="No education entries yet." />}
                    <div className="space-y-4">
                        {education.map((e, i) => (
                            <RowCard
                                key={i}
                                onRemove={() => setEducation((arr) => arr.filter((_, k) => k !== i))}
                                testid={`resume-edu-${i}`}
                            >
                                <TextInput label="Institution *" value={e.institution} onChange={(v) => updateAt(setEducation, i, "institution", v)} testid={`resume-edu-${i}-institution`} />
                                <TextInput label="Location" value={e.location} onChange={(v) => updateAt(setEducation, i, "location", v)} testid={`resume-edu-${i}-location`} placeholder="Cambridge, MA" />
                                <TextInput label="Degree" value={e.degree} onChange={(v) => updateAt(setEducation, i, "degree", v)} testid={`resume-edu-${i}-degree`} placeholder="Bachelor of Arts" />
                                <TextInput label="Concentration" value={e.concentration} onChange={(v) => updateAt(setEducation, i, "concentration", v)} testid={`resume-edu-${i}-concentration`} />
                                <TextInput label="GPA" value={e.gpa} onChange={(v) => updateAt(setEducation, i, "gpa", v)} testid={`resume-edu-${i}-gpa`} placeholder="3.8" />
                                <TextInput label="Dates" value={e.dates} onChange={(v) => updateAt(setEducation, i, "dates", v)} testid={`resume-edu-${i}-dates`} placeholder="Sept 2020 – May 2024" />
                                <TextArea label="Thesis" value={e.thesis} onChange={(v) => updateAt(setEducation, i, "thesis", v)} testid={`resume-edu-${i}-thesis`} />
                                <TextArea label="Relevant Coursework" value={e.coursework} onChange={(v) => updateAt(setEducation, i, "coursework", v)} testid={`resume-edu-${i}-coursework`} />
                                <TextArea label="Honors / Awards" value={e.honors} onChange={(v) => updateAt(setEducation, i, "honors", v)} testid={`resume-edu-${i}-honors`} />
                            </RowCard>
                        ))}
                    </div>
                </Section>

                {/* Work Experience */}
                <Section
                    title="Work Experience"
                    icon={<Briefcase className="w-4 h-4" />}
                    testid="resume-work-section"
                    onAdd={addWork}
                >
                    {work.length === 0 && <Empty label="No work entries yet." />}
                    <div className="space-y-4">
                        {work.map((w, i) => (
                            <BulletCard
                                key={i}
                                data={w}
                                onChange={(k, v) => updateAt(setWork, i, k, v)}
                                onRemove={() => setWork((arr) => arr.filter((_, k) => k !== i))}
                                labels={{ organization: "Organization *", role: "Position", location: "Location", dates: "Dates" }}
                                testid={`resume-work-${i}`}
                                orgKey="organization"
                                roleKey="position"
                            />
                        ))}
                    </div>
                </Section>

                {/* Leadership & Activities */}
                <Section
                    title="Leadership & Activities"
                    icon={<Users className="w-4 h-4" />}
                    testid="resume-leadership-section"
                    onAdd={addLead}
                >
                    {leadership.length === 0 && <Empty label="No leadership entries yet." />}
                    <div className="space-y-4">
                        {leadership.map((l, i) => (
                            <BulletCard
                                key={i}
                                data={l}
                                onChange={(k, v) => updateAt(setLeadership, i, k, v)}
                                onRemove={() => setLeadership((arr) => arr.filter((_, k) => k !== i))}
                                labels={{ organization: "Organization *", role: "Role", location: "Location", dates: "Dates" }}
                                testid={`resume-lead-${i}`}
                                orgKey="organization"
                                roleKey="role"
                            />
                        ))}
                    </div>
                </Section>

                {/* Skills & Interests */}
                <Section title="Skills & Interests" icon={<Sparkles className="w-4 h-4" />} testid="resume-skills-section">
                    <div className="space-y-3">
                        <TextArea label="Technical" value={skills.technical} onChange={(v) => setSkills((s) => ({ ...s, technical: v }))} testid="resume-skills-technical" placeholder="Languages, frameworks, tools…" />
                        <TextArea label="Languages" value={skills.languages} onChange={(v) => setSkills((s) => ({ ...s, languages: v }))} testid="resume-skills-languages" placeholder="French (fluent), Spanish (conversational)" />
                        <TextArea label="Laboratory" value={skills.laboratory} onChange={(v) => setSkills((s) => ({ ...s, laboratory: v }))} testid="resume-skills-laboratory" placeholder="PCR, cell culture, spectrometry…" />
                        <TextArea label="Interests" value={skills.interests} onChange={(v) => setSkills((s) => ({ ...s, interests: v }))} testid="resume-skills-interests" placeholder="Coastal restoration, bass guitar, chess" />
                    </div>
                </Section>

                <div className="mt-8 flex justify-end">
                    <button onClick={save} disabled={saving} className="arcade-btn" data-variant="primary" data-testid="resume-editor-save-bottom">
                        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save resume"}
                    </button>
                </div>
            </main>
        </div>
    );
}

function updateAt(setter, i, key, val) {
    setter((arr) => arr.map((row, k) => (k === i ? { ...row, [key]: val } : row)));
}

function Section({ title, icon, children, testid, onAdd }) {
    return (
        <section className="mt-8 bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-black text-lg tracking-tight text-slate-900 inline-flex items-center gap-2">
                    {icon} {title}
                </h2>
                {onAdd && (
                    <button type="button" onClick={onAdd} className="arcade-btn text-xs" data-testid={`${testid}-add`}>
                        <Plus className="w-4 h-4" /> Add
                    </button>
                )}
            </div>
            {children}
        </section>
    );
}

function Empty({ label }) {
    return <div className="text-sm text-slate-400 italic">{label}</div>;
}

function RowCard({ children, onRemove, testid }) {
    return (
        <div className="rounded-xl border border-slate-200 p-4" data-testid={testid}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
            <div className="mt-3 text-right">
                <button type="button" onClick={onRemove} className="text-xs text-sciences hover:underline inline-flex items-center gap-1" data-testid={`${testid}-remove`}>
                    <Trash2 className="w-3 h-3" /> Remove
                </button>
            </div>
        </div>
    );
}

function BulletCard({ data, onChange, onRemove, labels, testid, orgKey, roleKey }) {
    const bullets = data.bullets || [];
    const setBullet = (idx, val) => {
        const next = [...bullets];
        next[idx] = val;
        onChange("bullets", next);
    };
    const addBullet = () => onChange("bullets", [...bullets, ""]);
    const rmBullet = (idx) => onChange("bullets", bullets.filter((_, k) => k !== idx));

    return (
        <div className="rounded-xl border border-slate-200 p-4" data-testid={testid}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextInput label={labels.organization} value={data[orgKey]} onChange={(v) => onChange(orgKey, v)} testid={`${testid}-org`} />
                <TextInput label={labels.role} value={data[roleKey]} onChange={(v) => onChange(roleKey, v)} testid={`${testid}-role`} />
                <TextInput label={labels.location} value={data.location} onChange={(v) => onChange("location", v)} testid={`${testid}-location`} placeholder="Boston, MA · Remote" />
                <TextInput label={labels.dates} value={data.dates} onChange={(v) => onChange("dates", v)} testid={`${testid}-dates`} placeholder="Jun 2023 – Aug 2024" />
            </div>
            <div className="mt-3">
                <div className="text-xs font-display font-bold uppercase tracking-widest text-slate-500 mb-1">
                    Bullets — start each with an action verb
                </div>
                {bullets.map((b, i) => (
                    <div key={i} className="flex items-start gap-2 mt-1.5">
                        <span className="text-slate-400 mt-2.5">•</span>
                        <textarea
                            className="field-input text-sm flex-1 py-2"
                            rows={1}
                            value={b}
                            onChange={(e) => setBullet(i, e.target.value)}
                            placeholder="Delivered X to Y, resulting in Z."
                            data-testid={`${testid}-bullet-${i}`}
                        />
                        <button type="button" onClick={() => rmBullet(i)} className="text-slate-400 hover:text-sciences mt-2" data-testid={`${testid}-bullet-${i}-remove`}>
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ))}
                <button type="button" onClick={addBullet} className="mt-2 text-xs text-humanities font-bold hover:underline inline-flex items-center gap-1" data-testid={`${testid}-add-bullet`}>
                    <Plus className="w-3 h-3" /> Add bullet
                </button>
            </div>
            <div className="mt-3 text-right">
                <button type="button" onClick={onRemove} className="text-xs text-sciences hover:underline inline-flex items-center gap-1" data-testid={`${testid}-remove`}>
                    <Trash2 className="w-3 h-3" /> Remove entry
                </button>
            </div>
        </div>
    );
}

function TextInput({ label, value, onChange, testid, placeholder }) {
    return (
        <label className="block">
            <span className="text-xs font-display font-bold uppercase tracking-widest text-slate-500">{label}</span>
            <input
                type="text"
                className="field-input mt-1 text-sm py-2"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
            />
        </label>
    );
}

function TextArea({ label, value, onChange, testid, placeholder }) {
    return (
        <label className="block sm:col-span-2">
            <span className="text-xs font-display font-bold uppercase tracking-widest text-slate-500">{label}</span>
            <textarea
                className="field-input mt-1 text-sm py-2"
                rows={2}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
            />
        </label>
    );
}
